import { BondSeries, SeriesWithOrders } from './deal-detail.types';

export interface PurposeMapping {
  PURPOSE_CODE: string;
  PURPOSE_DESCRIPTION: string;
  MAPPED_SECTOR?: string;
}

const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const pad2 = (n: number): string => String(n).padStart(2, '0');

const formatUTCDate = (valueMs: number): string => {
  const d = new Date(valueMs);
  const month = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const year = d.getUTCFullYear();
  return `${month}/${day}/${year}`;
};

const formatUTCDateTime = (valueMs: number): string => {
  const d = new Date(valueMs);
  const hours = pad2(d.getUTCHours());
  const minutes = pad2(d.getUTCMinutes());
  const seconds = pad2(d.getUTCSeconds());
  return `${formatUTCDate(valueMs)} ${hours}:${minutes}:${seconds}`;
};

export const getFormattedDate = (d: Date): string => {
  const day = days[d.getUTCDay()];
  const month = d.getUTCMonth() + 1;
  const dayOfMonth = d.getUTCDate();

  return `${day} ${month}/${dayOfMonth}`;
};

/**
 * Utility function to get deal-level field value from series data.
 * Returns:
 * - Actual value if single series or all series have same value
 * - "Various" if multiple series have different values
 * - undefined if all values are null/undefined (for blank display)
 */
export function getSeriesFieldValue<T extends keyof BondSeries>(
  seriesList: BondSeries[],
  fieldName: T
): string | number | undefined {
  if (!seriesList || seriesList.length === 0) {
    return undefined;
  }

  // Single series: return value or undefined if null/undefined
  if (seriesList.length === 1) {
    return seriesList[0][fieldName];
  }

  // Multiple series: get all non-null values
  const values = seriesList
    .map(series => series[fieldName])
    .filter(value => value !== null && value !== undefined);

  // All nulls: return undefined (blank)
  if (values.length === 0) {
    return undefined;
  }

  // Check if all non-null values are the same
  const uniqueValues = [...new Set(values)];
  return uniqueValues.length === 1 ? uniqueValues[0] : 'Various';
}

/**
 * Special handler for Trade Date which includes time component.
 * Compares both date AND time.
 * Returns: date value, "Various", or undefined
 */
export function getTradeDateValue(
  seriesList: BondSeries[]
): number | string | undefined {
  if (!seriesList || seriesList.length === 0) {
    return undefined;
  }

  if (seriesList.length === 1) {
    return seriesList[0].TRADE_DATE;
  }

  // Get all non-null trade dates
  const tradeDates = seriesList
    .map(series => series.TRADE_DATE)
    .filter(value => value !== null && value !== undefined);

  if (tradeDates.length === 0) {
    return undefined;
  }

  // Compare date+time in UTC so "Various" rollups are timezone-invariant.
  const parsedDates = tradeDates.map((val) => new Date(val as number));
  const isoStrings = parsedDates.map((d) => d.toISOString());
  const uniqueIsoStrings = [...new Set(isoStrings)];

  return uniqueIsoStrings.length === 1 ? (tradeDates[0] as number) : 'Various';
}

/**
 * Special handler for date fields (excluding Trade Date).
 * Compares dates only (ignores time).
 * Returns: date value, "Various", or undefined
 */
export function getDateFieldValue(
  seriesList: BondSeries[],
  fieldName: 'DATED_DATE' | 'DELIVERY_DATE' | 'FIRST_COUPON_DATE' | 'AWARD_DATE'
): number | string | undefined {
  if (!seriesList || seriesList.length === 0) {
    return undefined;
  }

  if (seriesList.length === 1) {
    return seriesList[0][fieldName];
  }

  // Get all non-null date values
  const dateValues = seriesList
    .map(series => series[fieldName])
    .filter(value => value !== null && value !== undefined);

  if (dateValues.length === 0) {
    return undefined;
  }

  // Compare date components only in UTC (ignore time).
  const parsedDates = dateValues.map((val) => new Date(val as number));
  const dateStrings = parsedDates.map((d) => d.toISOString().slice(0, 10)); // YYYY-MM-DD UTC
  const uniqueDates = [...new Set(dateStrings)];

  return uniqueDates.length === 1 ? dateValues[0] : 'Various';
}

/**
 * Check if field should be displayed at series level.
 * 
 * Display rules:
 * - Returns true ONLY when deal level shows "Various" (different values across series)
 * - Returns false when:
 *   - Single series (always show at deal level only)
 *   - All series have same value (deal level shows the value)
 *   - All series have null values (deal level shows blank)
 *   - Some series have same value, rest are null (deal level shows the value)
 */
export function shouldDisplayAtSeriesLevel<T extends keyof BondSeries>(
  seriesList: BondSeries[],
  fieldName: T
): boolean {
  if (!seriesList || seriesList.length <= 1) {
    return false; // Single series: never show at series level
  }

  const dealValue = getSeriesFieldValue(seriesList, fieldName);
  
  // Only show at series level when deal level shows "Various"
  // This means different values exist across series
  return dealValue === 'Various';
}


// Common helper to format date value with optional time
// If withTime is true, formats as "MM/DD/YYYY (HH:MM:SS)"
// If withTime is false, formats as "MM/DD/YYYY"
export const formatDateOrBlank = (
  value: string | number | undefined, 
  withTime: boolean = false
): string => {
  if (value === undefined || value === null) {
    return '';
  }
  if (value === 'Various') {
    return 'Various';
  }
  const asNumber = typeof value === 'number' ? value : Number(value);
  if (!asNumber || Number.isNaN(asNumber)) {
    return '';
  }

  return withTime ? formatUTCDateTime(asNumber) : formatUTCDate(asNumber);
};

// Helper to format regular field value or show blank/Various
export const formatFieldOrBlank = (value: string | number | undefined): string => {
  if (value === undefined || value === null) {
    return '';
  }
  if (value === 'Various') {
    return 'Various';
  }
  return String(value);
};

export const getPurposeDisplayValue = (
  seriesList: BondSeries[],
  purposeMappings: PurposeMapping[]
): string | undefined => {
  if (!seriesList || seriesList.length === 0) {
    return undefined;
  }

  const descriptions = seriesList
    .map((series) => {
      const purposeCode = series.PURPOSE?.trim().toUpperCase();
      if (!purposeCode) {
        return undefined;
      }
      const match = (purposeMappings || []).find(
        (mapping) => mapping.PURPOSE_CODE?.trim().toUpperCase() === purposeCode
      );
      return match?.PURPOSE_DESCRIPTION || series.PURPOSE;
    })
    .filter((description) => description !== undefined);

  if (descriptions.length === 0) {
    return undefined;
  }

  const uniqueDescriptions = [...new Set(descriptions)];
  return uniqueDescriptions.length === 1 ? uniqueDescriptions[0] : 'Various';
};

export const getPurposeSectorDisplayValue = (
  seriesList: BondSeries[],
  purposeMappings: PurposeMapping[]
): string | undefined => {
  const purposeValue = getPurposeDisplayValue(seriesList, purposeMappings);

  if (purposeValue === undefined) {
    return undefined;
  }

  if (purposeValue === 'Various') {
    return 'Various / Various';
  }

  const purposeCode = seriesList
    .map((s) => s.PURPOSE?.trim().toUpperCase())
    .find((code) => !!code);

  const match = purposeCode
    ? (purposeMappings || []).find(
        (m) => m.PURPOSE_CODE?.trim().toUpperCase() === purposeCode
      )
    : undefined;

  const sector = match?.MAPPED_SECTOR;

  return sector ? `${sector} / ${purposeValue}` : purposeValue;
};

export interface SeriesInfoItem {
  label: string;
  value: string;
  fullValue?: string; // Full value for tooltip when truncated
}

/**
 * Creates an array of series information items for display.
 * Only includes fields that differ across series (when bondSeriesList.length > 1).
 * Always includes Call Features and any additional labels.
 */
export function createSeriesInfoItems(
  bondSeriesList: BondSeries[],
  series: BondSeries,
  additionalLabels: Array<{ label: string; value: string }> = []
): SeriesInfoItem[] {
  const items: SeriesInfoItem[] = [];

  if (!bondSeriesList || !series || bondSeriesList.length <= 1) {
    // If single series or no data, only show Call Features and additional labels
    const callFeatureDescription = series?.CALL_FEATURE_DESCRIPTION || '';
    const truncatedCallFeature = callFeatureDescription.length > 200
      ? callFeatureDescription.substring(0, 200) + '...'
      : callFeatureDescription;

    items.push({
      label: 'Call Features',
      value: truncatedCallFeature,
      fullValue: callFeatureDescription, // Store full value for tooltip
    });
    items.push(...additionalLabels);
    return items;
  }

  // Define field configurations
  const fieldConfigs: Array<{
    label: string;
    fieldName: keyof BondSeries;
  }> = [
    { label: 'Tax Type', fieldName: 'TAX_STATUS' },
    { label: 'Interest Type', fieldName: 'INTEREST_TYPE' },
    { label: 'Issue Type', fieldName: 'SERIES_MONEY_TYPE' },
    { label: 'Security Type', fieldName: 'SECURITY_TYPE' },
    { label: 'Purpose', fieldName: 'PURPOSE' },
    { label: 'Source of Repayment', fieldName: 'SOURCE_OF_REPAYMENT' },
    { label: 'Moody\'s Rating', fieldName: 'SERIES_MOODYS_RATING' },
    { label: 'S&P Rating', fieldName: 'SERIES_SANDP_RATING' },
    { label: 'Fitch Rating', fieldName: 'SERIES_FITCH_RATING' },
    { label: 'Kroll Rating', fieldName: 'SERIES_KROLL_RATING' },
    { label: 'Moody\'s Outlook', fieldName: 'RATING_MOODYS_OUTLOOK' },
    { label: 'S&P Outlook', fieldName: 'RATING_SANDP_OUTLOOK' },
    { label: 'Fitch Outlook', fieldName: 'RATING_FITCH_OUTLOOK' },
    { label: 'Kroll Outlook', fieldName: 'RATING_KROLL_OUTLOOK' },
    { label: 'Enhancement', fieldName: 'ENHANCEMENT' },
  ];

  // Process standard fields
  fieldConfigs.forEach(({ label, fieldName }) => {
    if (shouldDisplayAtSeriesLevel(bondSeriesList, fieldName)) {
      items.push({
        label,
        value: String(series[fieldName] || ''),
      });
    }
  });

  // Handle special date fields
  const tradeDateValue = getTradeDateValue(bondSeriesList);
  if (tradeDateValue === 'Various') {
    items.push({
      label: 'Trade Date (Time)',
      value: formatDateOrBlank(series?.TRADE_DATE, true),
    });
  }

  const dateFields: Array<{
    label: string;
    fieldName: 'DATED_DATE' | 'DELIVERY_DATE' | 'FIRST_COUPON_DATE' | 'AWARD_DATE';
  }> = [
    { label: 'Dated Date', fieldName: 'DATED_DATE' },
    { label: 'Delivery Date', fieldName: 'DELIVERY_DATE' },
    { label: 'Award Date', fieldName: 'AWARD_DATE' },
    { label: 'First Coupon Date', fieldName: 'FIRST_COUPON_DATE' },
  ];

  dateFields.forEach(({ label, fieldName }) => {
    const dateValue = getDateFieldValue(bondSeriesList, fieldName);
    if (dateValue === 'Various') {
      items.push({
        label,
        value: formatDateOrBlank(series[fieldName], false),
      });
    }
  });

  // Always show Call Features (truncated to 200 chars with ellipsis if longer)
  const callFeatureDescription = series?.CALL_FEATURE_DESCRIPTION || '';
  const truncatedCallFeature = callFeatureDescription.length > 200
    ? callFeatureDescription.substring(0, 200) + '...'
    : callFeatureDescription;

  items.push({
    label: 'Call Features',
    value: truncatedCallFeature,
    fullValue: callFeatureDescription, // Store full value for tooltip
  });

  // Add additional labels
  items.push(...additionalLabels);

  return items;
}
